/**
 * ehr#437 part 6 (PROGRESS_NOTE_10-14-V81 Last session, History, copy forward, plan follow-up):
 * the client's past notes as the note's rail reads them. Pure, so it's tested on its own.
 */
import { isOtherKind, noteBlocks } from './noteLayouts.js';
import { isNoBillingKind, noteTypeOf } from './noteTypes.js';

export interface PastNote {
  id: string;
  authorId: string;
  date: string;
  sessionLabel: string;
  format: string;
  isGroup: boolean;
  status: string;
  body: Record<string, unknown>;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');
export const pastKind = (n: PastNote) =>
  noteTypeOf({ isGroup: n.isGroup, sessionLabel: n.sessionLabel, noteType: n.body.noteType });

/**
 * ehr#571 (case management round, Notes Drawer V20 V57): may a note of kind `kind` feed the open
 * note's "From your notes" pills? An Other note (case management, peer support, ...) reads only its
 * own kind; a therapy note only therapy notes, so therapy and service sentences never mix.
 */
export function phraseKindOk(kind: string, openKind: string): boolean {
  return isOtherKind(openKind) ? kind === openKind : !isOtherKind(kind);
}

/** ehr#571 (V20 "+ Last session's plan"): the line the pill adds to the note's first box. */
export const lastPlanLine = (plan: string) => `Follow-up on last session's plan: ${plan}`;

/**
 * ehr#571 (V20 bringBoxId): the box the "+ Last session's plan" pill sits under: Subjective, Data
 * or Behavior when the note shows one (What happened on a Service note is Subjective renamed), else
 * the first text box; null when there is none.
 */
export function bringBoxOf(blocks: readonly { id: string; kind: string }[]): string | null {
  const text = blocks.filter((b) => b.kind === 'text');
  return (
    (text.find((b) => ['subjective', 'data', 'behavior'].includes(b.id)) ?? text[0])?.id ?? null
  );
}

/** Signed (or co-signed) notes other than the open one, newest first. */
export function signedNotes(notes: PastNote[], openId: string | null): PastNote[] {
  return notes
    .filter((n) => n.id !== openId && (n.status === 'signed' || n.status === 'cosigned'))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
}

/** A past note's text parts in its own layout's order, under its names (only ones with text). */
export function pastSections(n: PastNote): { key: string; label: string; text: string }[] {
  return noteBlocks(n.format, pastKind(n))
    .filter((b) => b.kind === 'text')
    .map((b) => ({ key: b.id, label: b.label, text: str(n.body[b.id]).trim() }))
    .filter((s) => s.text);
}

/** "10/03/2026 · Individual Therapy · 90834 · 50 min", the parts the note has. */
export function pastMeta(n: PastNote, fmtDate: (iso: string) => string): string {
  const s = (n.body.session ?? {}) as { code?: string; durationMin?: number };
  const kind = pastKind(n);
  return [
    fmtDate(n.date),
    n.sessionLabel,
    // ehr#708: a No billing kind shows no code (one saved before the editor hid the picker)
    isNoBillingKind(kind) ? '' : (s.code ?? ''),
    // ehr#713: a no-show has no length (only its start)
    s.durationMin && kind !== 'No-show / cancellation' ? `${s.durationMin} min` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The History list for a search, a kind and a date range (YYYY-MM-DD). */
export function filterPast(
  notes: PastNote[],
  f: { q: string; kind: string; from: string; to: string },
  authorName: (id: string) => string,
): PastNote[] {
  const q = f.q.trim().toLowerCase();
  return notes.filter((n) => {
    const d = String(n.date).slice(0, 10);
    if (f.kind && pastKind(n) !== f.kind) return false;
    if (f.from && d < f.from) return false;
    if (f.to && d > f.to) return false;
    if (!q) return true;
    const hay = [n.sessionLabel, authorName(n.authorId), ...pastSections(n).map((s) => s.text)]
      .join(' ')
      .toLowerCase();
    return q.split(/\s+/).every((w) => hay.includes(w));
  });
}

/** V81 appendText: copied text goes below what a box already has. */
export function appendText(cur: string, add: string): string {
  const c = cur.replace(/\s+$/, '');
  return c ? `${c}\n\n${add}` : add;
}

/**
 * V81 leftovers: the "Next session: …" lines of the last plan come back as one question each above
 * Plan ("Did you: identify early warning signs?").
 */
export function planFollowUps(plan: string): string[] {
  return plan
    .split(/(?<=[.!?])\s+|\n+/)
    .map((s) => s.trim())
    .map((s) => /^Next (?:session|contact|time|visit)\s*[:,]\s*(.+?)\.?$/i.exec(s)?.[1] ?? '')
    .filter(Boolean);
}

const lc = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
/** The sentence an answer writes into Plan (V81 leftoverWrite). */
export function followUpLine(item: string, ans: 'yes' | 'no', why = ''): string {
  return ans === 'yes'
    ? `Addressed from last session: ${lc(item)}.`
    : `Not addressed from last session: ${lc(item)}${why.trim() ? ` (${why.trim()})` : ''}.`;
}
/** Plan with an answer's sentence put in, replacing the sentence the last answer wrote. */
export function withFollowUp(plan: string, prevLine: string, line: string): string {
  if (prevLine && plan.includes(prevLine))
    return plan
      .replace(prevLine, line)
      .replace(/[ \t]{2,}/g, ' ')
      .trim();
  return plan.trim() ? `${plan.trim()} ${line}` : line;
}
